import type { IndustryPage } from '../industries';

const lines = [
  { item: '6 ft cedar privacy fence, 148 linear ft, 4x4 posts at 8 ft centers, set in concrete', amount: '$5,780' },
  { item: 'Single walk gate, 4 ft, steel frame kit, self-closing hinges and latch', amount: '$420' },
  { item: 'Tear out 150 ft of old chain link and haul to the transfer station', amount: '$600' },
  { item: 'Optional: clear stain and seal both sides once the wood has dried out', amount: '$890' },
];

const faq = [
  {
    q: 'Who is responsible for knowing where the property line is?',
    a: 'Put the answer in your terms. Most fence contractors build where the homeowner points or to a survey the homeowner provides, and say plainly that they do not locate boundaries. That one sentence prevents the most expensive fence disputes.',
  },
  {
    q: 'How do I price a fence when the yard has rock or roots?',
    a: 'Price the job for normal digging and add a clause that says what happens if a post hole hits rock, old concrete or large roots: you stop, explain, and agree on the extra before you continue.',
  },
  {
    q: 'Can the homeowner add staining after seeing the price?',
    a: 'Yes. Put staining, post caps or a second gate on the quote as optional lines in Scopie. The homeowner ticks what they want on the quote page, sees the new total, and signs.',
  },
  {
    q: 'Does Scopie lay out the fence on a map or count posts and panels?',
    a: 'No. There is no fence drawing or material takeoff in Scopie. You measure and price the run yourself; Scopie handles the quote, the signature, the install date and the invoice.',
  },
];

const page: IndustryPage = {
  slug: 'fencing',
  order: 5,
  group: 'Outdoor',
  path: '/for/fencing',
  primaryKeyword: 'fence contractor software',
  secondaryKeywords: ['fence estimate template', 'fence quote software', 'fence estimating software'],
  title: 'Fence Contractor Software for Quotes by the Run | Scopie',
  metaDescription:
    'Quote fences by run, height and gate, put property-line and rock clauses in writing, and get the homeowner to sign before you order a single post.', // 147
  h1: 'Fence quotes priced by the run, signed before you order posts',
  answerFirst:
    'Scopie lets fence contractors price each run by length, height and style, list gates and tear-out separately, and attach the clauses that matter in this trade: property lines, utility marking and rock. The homeowner opens the quote from a text message, picks any extras, and signs before materials are ordered.',
  exampleQuote: { label: 'Sample backyard fence quote', lines, total: '$7,690', depositNote: 'The deposit rule and the property-line clause come from the contractor\'s own terms.' },
  blocks: [
    {
      type: 'callout',
      id: 'property-line',
      text: 'Fence disputes rarely start with the fence. They start with where it stands. Write down who decides the line before anyone digs.',
    },
    {
      type: 'sample-quote',
      id: 'sample',
      h2: 'A backyard fence, line by line',
      label: 'Sample quote: cedar privacy fence with tear-out',
      lines,
      total: '$7,690',
      totalLabel: 'Total with staining chosen',
      note: 'Example numbers only. Staining is optional, so the homeowner can sign for the fence alone.',
    },
    {
      type: 'text',
      id: 'runs',
      h2: 'Price by run, not by yard',
      lead: 'One number for the whole backyard, and the neighbor\'s side run turned out to be twenty feet longer than you thought.',
      paragraphs: [
        'Break the fence into runs, each with its length in linear feet, the height and the style: privacy, picket, split rail, chain link, vinyl or aluminum. Terminal posts, corner posts and gates are where the money is, so give gates their own line with width and hardware.',
        'Tear-out and haul-away of the old fence deserve a separate line too. Homeowners often assume removal is free, and a competitor who leaves it off will look cheaper until the old fence is still lying in the yard.',
      ],
    },
    {
      type: 'list',
      id: 'before-digging',
      h2: 'Before the first post hole: what the quote should settle',
      style: 'checklist',
      items: [
        { text: 'Who calls 811 for utility marking, and how long you wait for the marks.' },
        { text: 'Who confirms the boundary: a survey, pins the homeowner shows you, or the homeowner\'s instruction.' },
        { text: 'HOA approval and any permit, and who applies.' },
        { text: 'Which side faces out, and how the fence follows a slope: stepped or racked.' },
        { text: 'What happens with rock, roots, buried concrete or sprinkler lines.' },
        { text: 'Where the gate swings and whether it must clear a mower.' },
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Where fence quotes lose money',
      style: 'mistakes',
      items: [
        { title: 'Ordering before a deposit is agreed', text: 'Cedar, vinyl and aluminum are bought for one job. Put your deposit rule on the quote so it is signed before the order goes in.' },
        { title: 'Gates priced like fence', text: 'A gate takes longer than a panel and needs heavier posts and hardware. Price it separately.' },
        { title: 'No slope allowance', text: 'Racking or stepping on a grade adds labor and material. Say which method and price it in.' },
        { title: 'Validity dates left off', text: 'Lumber prices move. Give each quote an end date.' },
        { title: 'Silence on concrete cure time', text: 'Customers want to know when the dog can go out. Tell them.' },
      ],
    },
    {
      type: 'text',
      id: 'spring',
      h2: 'Spring rush and frozen ground',
      paragraphs: [
        'Fence demand peaks in spring and early summer, when everyone wants the yard closed in before the pool opens or the new dog arrives. Quotes that sit unsigned in April become installs you cannot fit in until July. Winter brings frozen ground in the north and longer lead times on vinyl and aluminum everywhere.',
        'A quote the homeowner can open and sign tonight, with your lead time written on it, keeps your spring calendar honest. Once signed, the install goes on the schedule with the crew and the auger or skid steer it needs, so the whole team can see which crew has the auger that day.',
      ],
    },
    {
      type: 'text',
      id: 'after',
      h2: 'Invoices, and the storm call two years later',
      paragraphs: [
        'When the last picket is up, send the invoice as a link. The homeowner stays in your client list with the quote they signed, so when a storm takes out a section, you can see the exact style, height and stain and quote the repair in minutes.',
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Fence quoting questions', items: faq },
  ],
  sections: [],
  faq,
  internalLinks: [
    { href: '/for', anchor: 'All industries', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
    { href: '/for/landscaping', anchor: 'Scopie for landscaping businesses', status: 'live' },
    { href: '/for/concrete', anchor: 'Scopie for concrete contractors', status: 'live' },
    { href: '/templates/fence-estimate-template', anchor: 'free fence estimate template', status: 'live' },
  ],
  cta: {
    heading: 'Close the spring backlog faster',
    text: 'Load your fence styles, gates and clauses into Scopie once, then quote each yard from the truck.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
  claimsToVerify: [],
};

export default page;
